import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { isDefined } from '@trimble-gcs/common';
import { ClearClipRegion, SetActiveMapTool, SetBounds, SetClipRegion, SetMapFilterOption } from './map.actions';
import {
  DEFAULT_BBOX,
  DEFAULT_BOUNDS_PADDING,
  MapBounds,
  MapClipRegion,
  MapFilterOption,
  MapTool,
} from './map.models';

export interface MapStateModel {
  filterOption: MapFilterOption;
  activeMapTool: MapTool;
  bounds: MapBounds;
  clipRegion?: MapClipRegion;
}

const defaultState: MapStateModel = {
  filterOption: MapFilterOption.LocatedOnly,
  activeMapTool: MapTool.Default,
  bounds: { bbox: DEFAULT_BBOX, padding: DEFAULT_BOUNDS_PADDING },
};

@State<MapStateModel>({
  name: 'MapState',
  defaults: defaultState,
})
@Injectable()
export class MapState {
  @Selector()
  static filterOption(state: MapStateModel) {
    return state.filterOption;
  }

  @Selector()
  static activeMapTool(state: MapStateModel) {
    return state.activeMapTool;
  }

  @Selector()
  static bounds(state: MapStateModel) {
    return state.bounds;
  }

  @Selector()
  static clipRegion(state: MapStateModel): MapClipRegion | undefined {
    return state.clipRegion;
  }

  @Action(SetMapFilterOption)
  setMapFilterOption(ctx: StateContext<MapStateModel>, { filterOption }: SetMapFilterOption) {
    ctx.patchState({ filterOption });
  }

  @Action(SetActiveMapTool)
  setActiveMapTool(ctx: StateContext<MapStateModel>, { mapTool }: SetActiveMapTool) {
    const isSelectTool =
      mapTool === MapTool.RectangleSelect || mapTool === MapTool.PolygonSelect;

    ctx.patchState({
      activeMapTool: mapTool,
      ...(isSelectTool && isDefined(ctx.getState().clipRegion) ? { clipRegion: undefined } : {}),
    });
  }

  @Action(SetBounds)
  setBounds(ctx: StateContext<MapStateModel>, { bounds }: SetBounds) {
    ctx.patchState({ bounds });
  }

  @Action(SetClipRegion)
  setClipRegion(ctx: StateContext<MapStateModel>, { clipRegion }: SetClipRegion) {
    ctx.patchState({ clipRegion });
  }

  @Action(ClearClipRegion)
  clearClipRegion(ctx: StateContext<MapStateModel>) {
    ctx.patchState({ clipRegion: undefined });
  }
}
